/**
 * This module contains all the hero data for the game.
 *
 * Cultural framing per docs/card-rename-mapping.md. Hero IDs are kept stable
 * (internal references); only display names and tribe assignments change.
 */

import { TribeId } from './cards/types';

/**
 * Type for unique hero identifiers.
 * Existing IDs preserved to avoid churn in tests/saves; new street-lane heroes added below.
 */
export type HeroId =
  // Existing 21 (renamed only, IDs stable)
  | "teacher"
  | "guitar_teacher"
  | "audio_guy"
  | "club_owner"
  | "og_hommie"
  | "barrio_godfather"
  | "old_school_punk"
  | "indie_dj"
  | "piercing_artist"
  | "scene_guardian"
  | "local_promoter"
  | "lowrider_mechanic"
  | "tattoo_master"
  | "neighborhood_watcher"
  | "street_barber"
  | "old_school_writer"
  | "block_chef"
  | "record_store_owner"
  | "block_captain"
  | "festival_organizer"
  | "detail_specialist"
  // New street-lane heroes (9, filling underfilled matrix cells)
  | "el_tagger"
  | "la_bomba"
  | "el_del_slam"
  | "la_aventada"
  | "el_chofer"
  | "el_generador"
  | "el_paletero"
  | "la_voz_nueva"
  | "don_hidraulico"
  // RUSH lane (tier-up economy)
  | "la_patrona";

/**
 * Type defining the different kinds of hero powers available.
 */
export type HeroPowerType =
  | "passive_on_buy"
  | "passive_frontline_health"
  | "passive_first_strike"
  | "passive_on_refresh"
  /** Unclaimed since La Aguja was re-rolled. Permanent +1/+1 for gold is a
      BANK effect, so whoever picks this up next should be a cholo. */
  | "active_target_buff"
  | "active_stat_swap"
  | "passive_healing"
  | "passive_end_of_turn"
  | "passive_start_of_combat_scout"
  | "passive_stat_swap"
  | "passive_on_damage_dealt"
  | "active_health_double"
  | "passive_shop_discount"
  | "passive_summon_token"
  | "passive_gold_generation"
  | "active_freeze_shop_unit"
  // New types for street-lane heroes
  | "passive_on_combat_end_buff"        // El Tagger
  | "passive_start_of_combat_damage"    // El del Slam
  | "active_move_and_buff"              // La Aventada
  | "passive_loss_refresh"              // El Chofer del Toquín
  | "passive_token_buff"                // El Paletero
  | "passive_start_of_combat_attack"    // La Voz Nueva
  | "passive_tier_rush"
  // --- bank/burn rework (see docs/hero-archive-v1.md) ---
  /** Cholo bank: end the turn holding gold, the whole crew grows permanently. */
  | "passive_unspent_gold_invest"
  /** Rocker burn: trade a unit's safety for damage, right now. */
  | "active_reckless_buff"
  /** Rocker burn: the LAST unit in line swings huge, this combat only. */
  | "passive_start_of_combat_backline"
  /** Rocker burn + opponent interaction: blunt the enemy's biggest hitter. */
  | "passive_start_of_combat_enemy_debuff"
  // --- axis pass two ---
  /** Rocker burn, paid in blood: Health is the currency, the high lasts one set. */
  | "active_blood_price"
  /** Rocker burn: crank it, the whole crew goes loud for one combat. */
  | "active_crew_surge"
  /** Rocker burn + tribe payoff: book one scene and the whole scene shows up. */
  | "passive_tribe_surge";

/**
 * Interface representing a single Hero's data.
 */
export interface Hero {
  id: HeroId;
  name: string;
  powerDescription: string;
  faction: "emo" | "cholo";
  tribe?: TribeId;
  powerType: HeroPowerType;
  powerConfig: Record<string, any>;
  isActive: boolean;
  /** Shelved for post-MVP. Hero stays in data but is filtered out of the active draft pool. */
  disabled?: boolean;
  cost?: number;
  targeting?: "none" | "friendly_board" | "shop_unit";
  portrait?: string;
  fullPortrait?: string;
}

/**
 * An array containing all hero objects available in the game.
 */
export const allHeroes: Hero[] = [
  // --- Emos Faction ---
  {
    id: "teacher",
    disabled: true,
    name: "El Profe de la Tocada",
    powerDescription: "Passive: After you refresh the shop, give a random shop unit +1/+1.",
    faction: "emo",
    tribe: "la_tocada",
    powerType: "passive_on_refresh",
    powerConfig: { attack: 1, health: 1 },
    isActive: true,
    // No portrait: the paths that used to sit here ("portraits/teacher%20headshot.png")
    // pointed at files that were never committed, so HeroCard fell through to a broken
    // <img> instead of the procedural initials stencil. Hero is shelved anyway
    // (`disabled`), so it goes back on the queue if it ever re-enters the draft pool.
  },
  {
    // BURN. The opening act plays loud and leaves. Buys attack with health,
    // which is the reckless trade the pit rewards. Deliberately the mirror of
    // La Aguja: same 1g, but she banks +1/+1 permanently where he spends
    // durability for damage now. Refuses 1-Health targets so it stays a
    // decision rather than a way to delete your own unit.
    id: "guitar_teacher",
    name: "El Telonero",
    powerDescription: "Active (1g): Give a unit +3 Attack and -1 Health.",
    faction: "emo",
    tribe: "slameros",
    powerType: "active_reckless_buff",
    powerConfig: { attack: 3, health: -1 },
    cost: 1,
    // He shipped without this in the first pass; the store routes off
    // classifyPower so nothing broke, but the hero card was the only
    // targeted active in the roster that did not declare its target.
    targeting: "friendly_board",
    isActive: true,
    portrait: "portraits/guitar_teacher.webp",
  },
  {
    id: "audio_guy",
    disabled: true,
    name: "El Sonidista",
    powerDescription: "Passive: Your leftmost unit has +3 Health during combat.",
    faction: "emo",
    tribe: "la_tocada",
    powerType: "passive_frontline_health",
    powerConfig: { health: 3, count: 1 },
    isActive: true,
  },
  {
    // BURN, and the roster's first price paid in blood. She used to hand out
    // a permanent +1/+1 — a placa on a rocker, the one power that argued the
    // wrong side of the axis. Moving her to cholo was the other option and it
    // was worse: the needle here is the piercing gun at 1am, adrenaline and a
    // little blood, not the years-long commitment La Tinta already owns.
    // Gold is deliberately NOT the cost. Every other power in the game is
    // bought with the tavern's currency; this one is bought with the only
    // resource you cannot earn back, which is what makes it a real gamble
    // rather than a rounding error on your buy.
    id: "piercing_artist",
    name: "La Aguja",
    powerDescription: "Active (0g): Pay 2 Health. A friendly unit gets +4/+4 this combat.",
    faction: "emo",
    tribe: "slameros",
    powerType: "active_blood_price",
    powerConfig: { healthCost: 2, attack: 4, health: 4 },
    isActive: true,
    cost: 0,
    targeting: "friendly_board",
    portrait: "portraits/piercing_artist.webp",
  },
  {
    // BURN + the roster's first tribe payoff. She books the lineup, and a
    // lineup pulled from one scene brings that whole scene through the door.
    // The threshold rhymes with the tribe breakpoints (2/4/6) on purpose: at
    // 4 you are already collecting a synergy bonus, and she pays you again
    // for the same commitment instead of asking for a second, unrelated one.
    // Rocker, so the reward is the night's — it lands on the combat copy.
    id: "club_owner",
    name: "La Dueña del Foro",
    powerDescription: "Passive: Start of combat, if 4+ of your units share a tribe, each of them gains +2 Attack this combat.",
    faction: "emo",
    tribe: "anclas",
    powerType: "passive_tribe_surge",
    powerConfig: { tribeThreshold: 4, attack: 2 },
    isActive: true,
    portrait: "portraits/club_owner.webp",
  },
  {
    id: "old_school_punk",
    disabled: true,
    name: "La Veterana del Toquín",
    powerDescription: "Passive: Your leftmost unit always attacks first in combat.",
    faction: "emo",
    tribe: "slameros",
    powerType: "passive_first_strike",
    powerConfig: {},
    isActive: true,
    portrait: "portraits/old_school_punk.webp",
  },
  {
    // BURN. The last song of the night. Every other combat power in the game
    // looks leftward, because combat resolves left to right — this one pays
    // the unit that swings LAST, so it asks a board-shape question nothing
    // else asks. Temporary by construction: it mutates the combat copy, so
    // the surge is gone when the set ends.
    id: "indie_dj",
    name: "La Última Rola",
    powerDescription: "Passive: Start of combat, your rightmost unit gains +5 Attack this combat.",
    faction: "emo",
    tribe: "la_tocada",
    powerType: "passive_start_of_combat_backline",
    powerConfig: { attack: 5 },
    isActive: true,
    portrait: "portraits/indie_dj.webp",
  },
  {
    id: "scene_guardian",
    disabled: true,
    name: "La Tía del Backyard",
    powerDescription: "Passive: At the end of your turn, heal your hero for 1.",
    faction: "emo",
    tribe: "anclas",
    powerType: "passive_healing",
    powerConfig: { amount: 1 },
    isActive: true,
  },
  {
    // BURN + the first power that reaches across the table. Everything else
    // in the roster only ever touched your own side; scouting could look at
    // the other crew but never touch them. Cutting the power to the other
    // band is the flavour and the mechanic at once.
    id: "local_promoter",
    name: "El del Apagón",
    powerDescription: "Passive: Start of combat, the enemy's highest-Attack unit loses 3 Attack this combat.",
    faction: "emo",
    tribe: "anclas",
    powerType: "passive_start_of_combat_enemy_debuff",
    powerConfig: { attack: 3 },
    isActive: true,
    portrait: "portraits/local_promoter.webp",
  },
  {
    id: "record_store_owner",
    disabled: true,
    name: "Don Discos",
    powerDescription: "Passive: The first unit you buy each turn costs 1 less gold (minimum 0).",
    faction: "emo",
    tribe: "la_tocada",
    powerType: "passive_shop_discount",
    powerConfig: { discount: 1 },
    isActive: true,
    portrait: "portraits/record_store_owner.webp",
  },
  {
    id: "festival_organizer",
    name: "El Festivalero",
    powerDescription: "Passive: At the start of your turn, gain 1 gold (max 10).",
    faction: "emo",
    tribe: "anclas",
    powerType: "passive_gold_generation",
    powerConfig: { amount: 1 },
    isActive: true,
    portrait: "portraits/festival_organizer.webp",
  },

  // --- Cholo Faction ---
  {
    id: "og_hommie",
    disabled: true,
    name: "El Veterano del Club",
    powerDescription: "Passive: Your two leftmost units have +2 Health during combat.",
    faction: "cholo",
    tribe: "bajitos",
    powerType: "passive_frontline_health",
    powerConfig: { health: 2, count: 2 },
    isActive: true,
  },
  {
    id: "tattoo_master",
    name: "La Tinta",
    powerDescription: "Active (2g): Double a friendly unit's Health (if its Attack is 5 or less).",
    faction: "cholo",
    tribe: "plaqueros",
    powerType: "active_health_double",
    powerConfig: { maxAttack: 5 },
    isActive: true,
    cost: 2,
    targeting: "friendly_board",
    portrait: "portraits/tattoo_master.webp",
  },
  {
    // Stays benched: Don Hidráulico now carries active_stat_swap live, and a
    // free version of the same verb would be the strictly-simpler pick.
    id: "lowrider_mechanic",
    disabled: true,
    name: "El Mecánico Morro",
    powerDescription: "Active (0g): Swap a friendly unit's Attack and Health.",
    faction: "cholo",
    tribe: "bajitos",
    powerType: "active_stat_swap",
    powerConfig: { uses: 1 },
    isActive: true,
    cost: 0,
    targeting: "friendly_board",
  },
  {
    // BANK. The tanda is a rotating savings pool run on trust — you pay in,
    // the pot comes back around. The whole point is not spending. This is the
    // purest expression of the cholo half of the axis: it asks you to end a
    // turn holding gold, which every other pressure in the game argues
    // against, and pays permanent stats for the discipline.
    id: "barrio_godfather",
    name: "Don Tandas",
    powerDescription: "Passive: End your turn with 4+ unspent gold to give your whole crew +1/+1 permanently.",
    faction: "cholo",
    tribe: "plaqueros",
    powerType: "passive_unspent_gold_invest",
    powerConfig: { goldThreshold: 4, attack: 1, health: 1 },
    isActive: true,
    portrait: "portraits/barrio_godfather.webp",
  },
  {
    id: "neighborhood_watcher",
    disabled: true,
    name: "El Esquinero",
    powerDescription: "Passive: At the start of combat, gain +1 Attack for each enemy unit.",
    faction: "cholo",
    tribe: "la_clica",
    powerType: "passive_start_of_combat_scout",
    powerConfig: { attack: 1 },
    isActive: true,
  },
  {
    id: "street_barber",
    disabled: true,
    name: "Don Barbero",
    powerDescription: "Passive: At the start of combat, swap the Attack and Health of your rightmost unit.",
    faction: "cholo",
    tribe: "la_clica",
    powerType: "passive_stat_swap",
    powerConfig: {},
    isActive: true,
  },
  {
    id: "old_school_writer",
    name: "El Veterano de la Lata",
    powerDescription: "Passive: Whenever a friendly unit deals damage, give it +1 Attack permanently.",
    faction: "cholo",
    tribe: "plaqueros",
    powerType: "passive_on_damage_dealt",
    powerConfig: { attack: 1 },
    isActive: true,
    portrait: "portraits/old_school_writer.webp",
  },
  {
    id: "block_chef",
    disabled: true,
    name: "Doña Tamales",
    powerDescription: "Passive: At the end of your turn, give your units +1 Health.",
    faction: "cholo",
    tribe: "la_clica",
    powerType: "passive_end_of_turn",
    powerConfig: { health: 1 },
    isActive: true,
  },
  {
    id: "block_captain",
    name: "La Jefa de la Cuadra",
    powerDescription: "Passive: At the end of your turn, if you have 4 or fewer units, summon a 1/1 token.",
    faction: "cholo",
    tribe: "la_clica",
    powerType: "passive_summon_token",
    powerConfig: { attack: 1, health: 1, maxUnits: 4 },
    isActive: true,
    portrait: "portraits/block_captain.webp",
  },
  {
    id: "detail_specialist",
    name: "El Detallista",
    powerDescription: "Active (1g): Freeze a unit in the shop. It stays until bought or replaced.",
    faction: "cholo",
    tribe: "bajitos",
    powerType: "active_freeze_shop_unit",
    powerConfig: {},
    isActive: true,
    cost: 1,
    targeting: "shop_unit",
    portrait: "portraits/detail_specialist.webp",
  },

  // --- New Street-Lane Heroes (9) ---
  // Plaqueros Street
  {
    id: "el_tagger",
    name: "El Tagger",
    powerDescription: "Passive: At the end of each combat, give a random friendly unit +1/+1 permanently.",
    faction: "cholo",
    tribe: "plaqueros",
    powerType: "passive_on_combat_end_buff",
    powerConfig: { attack: 1, health: 1 },
    isActive: true,
    portrait: "portraits/el_tagger.webp",
  },
  {
    id: "la_bomba",
    disabled: true,
    name: "La Bomba",
    powerDescription: "Passive: The first unit you buy each turn gets +2 Attack permanently.",
    faction: "cholo",
    tribe: "plaqueros",
    powerType: "passive_on_buy",
    // `target: 'bought'` is what her card actually promises — the unit you
    // just bought, not a random one. It is config rather than an id branch
    // in heroPowers.ts so the perk belongs to the power, not the portrait.
    powerConfig: { attack: 2, health: 0, firstOnly: true, permanent: true, target: 'bought' },
    isActive: true,
  },
  // Slameros Street
  {
    id: "el_del_slam",
    disabled: true,
    name: "El del Slam",
    powerDescription: "Passive: At the start of combat, your leftmost unit deals 2 damage to the enemy leftmost unit.",
    faction: "emo",
    tribe: "slameros",
    powerType: "passive_start_of_combat_damage",
    powerConfig: { damage: 2, source: "leftmost", target: "enemy_leftmost" },
    isActive: true,
  },
  {
    // BURN, and the only power that changes the SHAPE of the board rather
    // than the numbers on it. Combat resolves left to right, so which unit
    // stands at the front is a decision the roster otherwise never lets you
    // revisit once the shop closes. She throws herself to the front of the
    // pit; the crowd carries her for one song and that is all.
    id: "la_aventada",
    name: "La Aventada",
    powerDescription: "Active (1g): Move a friendly unit to the leftmost slot. It gets +2 Attack this combat.",
    faction: "emo",
    tribe: "slameros",
    powerType: "active_move_and_buff",
    powerConfig: { attack: 2, health: 0, destination: "leftmost", duration: "combat" },
    isActive: true,
    cost: 1,
    targeting: "friendly_board",
    portrait: "portraits/la_aventada.webp",
  },
  // Anclas Street
  {
    id: "el_chofer",
    // Shelved: passive_loss_refresh needs a post-combat win/loss hook that
    // doesn't exist yet (see the stub list in heroPowers.ts).
    disabled: true,
    name: "El Chofer del Toquín",
    powerDescription: "Passive: When you lose a combat, you may refresh the shop once for free this turn.",
    faction: "emo",
    tribe: "anclas",
    powerType: "passive_loss_refresh",
    powerConfig: { uses: 1 },
    isActive: true,
    portrait: "portraits/el_chofer.webp",
  },
  {
    // BURN, and the roster's only button that needs no target — you press it
    // and the whole crew goes loud. He was a shop discount, which is the
    // blandest verb the old roster had and one the emo side already spoke
    // twice. The generator is the backyard show's whole premise: there is no
    // power on this block, so you bring your own, you run it until the gas
    // is gone, and everybody plays louder for exactly one set.
    id: "el_generador",
    name: "El del Generador",
    powerDescription: "Active (2g): Your whole crew gets +1/+1 this combat.",
    faction: "emo",
    tribe: "anclas",
    powerType: "active_crew_surge",
    powerConfig: { attack: 1, health: 1 },
    isActive: true,
    cost: 2,
    targeting: "none",
    portrait: "portraits/el_generador.webp",
  },
  // La Clica Street
  {
    id: "el_paletero",
    disabled: true,
    name: "El Paletero",
    powerDescription: "Passive: Friendly tokens you summon have +0/+1.",
    faction: "cholo",
    tribe: "la_clica",
    powerType: "passive_token_buff",
    powerConfig: { attack: 0, health: 1 },
    isActive: true,
  },
  // La Tocada Street
  {
    id: "la_voz_nueva",
    disabled: true,
    name: "La Voz Nueva",
    powerDescription: "Passive: At the start of combat, give a random friendly unit +2 Attack until end of combat.",
    faction: "emo",
    tribe: "la_tocada",
    powerType: "passive_start_of_combat_attack",
    powerConfig: { attack: 2, duration: "combat" },
    isActive: true,
  },
  // Bajitos Establishment
  {
    // BANK. Three gold is a real price and the swap is a real risk, but what
    // you get back is written on the card forever — the hydraulics stay on
    // the car. Deliberately the expensive, permanent counterpart to the emo
    // actives, which are cheap and evaporate; the cholo half of the roster
    // should make you pay up front and then never take it back.
    id: "don_hidraulico",
    name: "Don Hidráulico",
    powerDescription: "Active (3g): Swap a friendly unit's Attack and Health, then give it +2/+2.",
    faction: "cholo",
    tribe: "bajitos",
    powerType: "active_stat_swap",
    powerConfig: { uses: 1, buffAfter: { attack: 2, health: 2 } },
    isActive: true,
    cost: 3,
    targeting: "friendly_board",
    portrait: "portraits/don_hidraulico.webp",
  },

  // --- RUSH lane ---
  // The "tier up fast, drop big minions early" fantasy. Two effects stack
  // into one loop: upgrades get cheap enough to take every other turn, and
  // each upgrade immediately hands you a body from the tier you just
  // unlocked — so arriving early actually swings the board instead of
  // leaving you broke on an empty bench.
  {
    id: "la_patrona",
    name: "La Patrona del Swapmeet",
    powerDescription:
      "Passive: Tavern upgrades cost 3 less. When you upgrade, add a free unit of your new tier to your hand.",
    faction: "cholo",
    tribe: "bajitos",
    powerType: "passive_tier_rush",
    powerConfig: { discount: 3, grantUnitOfNewTier: true },
    isActive: true,
    portrait: "portraits/la_patrona.webp",
  },
];
